import React, { useEffect } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { BookOpen, FileSearch, Leaf, LogIn, Megaphone, ShieldCheck } from 'lucide-react';
import { useTenant } from '../../contexts/TenantContext';
import { isPublicConsultaEnabled, isPublicDenunciaEnabled } from '../../utils/publicPages';
import {
  PoweredByUseAmbiental,
  TenantBanner,
  TenantCoatMark,
} from '../Brand/TenantBrandMark';
import { tenantBrand } from '../../utils/tenantBranding';

const ORG_KIND = {
  MUNICIPALITY: 'Órgão municipal',
  STATE: 'Órgão estadual',
  COMPANY: 'Organização',
  PLATFORM: 'Plataforma',
};

const NAV = [
  { to: '/process-lookup', label: 'Consultar andamento', short: 'Consulta', icon: FileSearch },
  { to: '/public-complaint', label: 'Denúncia anônima', short: 'Denúncia', icon: Megaphone },
];

const PublicPageShell = ({
  eyebrow,
  title,
  lead,
  children,
  aside,
}) => {
  const { t } = useTranslation(['auth', 'common']);
  const { tenant } = useTenant();
  const orgName = tenant?.name || t('common:brandName');
  const brand = tenantBrand(tenant);
  const orgKind = ORG_KIND[tenant?.org_type] || null;
  const consultaOn = isPublicConsultaEnabled(tenant);
  const denunciaOn = isPublicDenunciaEnabled(tenant);
  const brandTo = consultaOn ? '/process-lookup' : denunciaOn ? '/public-complaint' : '/login';
  const navItems = NAV.filter((item) =>
    item.to === '/process-lookup' ? consultaOn : item.to === '/public-complaint' ? denunciaOn : true
  );

  useEffect(() => {
    const previous = document.title;
    document.title = `${title} — ${orgName}`;
    return () => {
      document.title = previous;
    };
  }, [title, orgName]);

  return (
    <div className="relative min-h-screen overflow-hidden bg-slate-950 text-white">
      <div
        className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_120%_80%_at_50%_-20%,rgba(46,125,50,0.38),transparent)]"
        aria-hidden
      />
      <div className="pointer-events-none absolute -left-32 top-24 h-96 w-96 rounded-full bg-primary-700/25 blur-3xl" aria-hidden />
      <div className="pointer-events-none absolute -right-24 bottom-0 h-80 w-80 rounded-full bg-emerald-600/20 blur-3xl" aria-hidden />
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.06] [background-image:linear-gradient(rgba(255,255,255,0.16)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.16)_1px,transparent_1px)] [background-size:48px_48px]"
        aria-hidden
      />

      <div className="relative z-10 flex min-h-screen flex-col">
        <header className="border-b border-white/10 bg-slate-950/70 backdrop-blur-md">
          {brand.bannerUrl ? (
            <div className="bg-white">
              <TenantBanner tenant={tenant} className="mx-auto h-16 w-full max-w-6xl" />
            </div>
          ) : null}
          <div className="mx-auto flex max-w-6xl flex-col gap-3 px-4 py-4 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between sm:px-6">
            <Link to={brandTo} className="flex items-center gap-3">
              {brand.coatOfArmsUrl || brand.logoUrl ? (
                <TenantCoatMark tenant={tenant} size={48} fallback={false} tone="dark" />
              ) : (
                <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-primary-500/20 ring-1 ring-primary-400/30">
                  <Leaf className="h-6 w-6 text-primary-300" strokeWidth={1.75} aria-hidden />
                </span>
              )}
              <span>
                <PoweredByUseAmbiental light className="block" />
                <span className="block text-sm font-semibold tracking-tight text-white sm:text-base">
                  {orgName}
                </span>
                {orgKind ? (
                  <span className="block text-[11px] text-slate-400">{orgKind} · canal oficial</span>
                ) : null}
              </span>
            </Link>
            <nav className="flex flex-wrap items-center gap-2" aria-label="Área pública">
              {navItems.map(({ to, label, short, icon: Icon }) => (
                <NavLink
                  key={to}
                  to={to}
                  className={({ isActive }) =>
                    `inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold transition ${
                      isActive
                        ? 'bg-white text-primary-900'
                        : 'bg-white/10 text-slate-200 ring-1 ring-white/10 hover:bg-white/15'
                    }`
                  }
                >
                  <Icon className="h-3.5 w-3.5" aria-hidden />
                  <span className="sm:hidden">{short}</span>
                  <span className="hidden sm:inline">{label}</span>
                </NavLink>
              ))}
              <a
                href="/docs/"
                className="inline-flex items-center gap-1.5 rounded-full bg-white/10 px-3 py-1.5 text-xs font-semibold text-slate-200 ring-1 ring-white/10 hover:bg-white/15"
              >
                <BookOpen className="h-3.5 w-3.5" aria-hidden />
                Manual
              </a>
              <Link
                to="/login"
                className="inline-flex items-center gap-1.5 rounded-full bg-primary-600 px-3 py-1.5 text-xs font-semibold text-white shadow-lg shadow-primary-900/30 hover:bg-primary-500"
              >
                <LogIn className="h-3.5 w-3.5" aria-hidden />
                Entrar
              </Link>
            </nav>
          </div>
        </header>

        <main className="mx-auto flex w-full max-w-6xl flex-1 flex-col gap-8 px-4 py-10 sm:px-6 lg:flex-row lg:py-14">
          <div className="min-w-0 flex-1">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-primary-200">{eyebrow}</p>
            <h1 className="mt-2 text-3xl font-bold tracking-tight text-white sm:text-4xl">{title}</h1>
            <p className="mt-3 max-w-2xl text-sm leading-relaxed text-slate-300 sm:text-base">{lead}</p>
            <div className="mt-8 overflow-hidden rounded-2xl border border-white/10 bg-white/[0.97] text-gray-900 shadow-strong">
              <div className="h-1 bg-gradient-to-r from-primary-600 via-emerald-500 to-primary-800" aria-hidden />
              <div className="p-5 sm:p-8">{children}</div>
            </div>
          </div>

          {aside ? (
            <aside className="w-full shrink-0 space-y-4 lg:w-80 xl:w-96">{aside}</aside>
          ) : null}
        </main>

        <footer className="border-t border-white/10 px-4 py-6 text-center text-xs text-slate-500 sm:px-6">
          <p className="inline-flex items-center justify-center gap-1.5">
            <ShieldCheck className="h-3.5 w-3.5 text-primary-300" aria-hidden />
            Canal oficial do {orgName} no useAmbiental. Não consulta SEI, DOU nem IBAMA.
          </p>
        </footer>
      </div>
    </div>
  );
};

export default PublicPageShell;
